import { HttpErrorResponse } from '@angular/common/http';

/**
 * A toast is either a translation key or the server's own words. `what` is
 * the key for what failed ("shipments"), filled into the key's `{{what}}`.
 */
export type HttpErrorToastMessage =
  | { key: string; what?: string }
  | { text: string };

const MAX_SERVER_MESSAGE_LENGTH = 160;

// Status texts and catch-alls that tell the user nothing.
const GENERIC_SERVER_MESSAGES = new Set([
  'bad request',
  'conflict',
  'error',
  'forbidden',
  'internal server error',
  'not found',
  'server error',
  'something went wrong',
  'unknown error',
  'unprocessable entity'
]);

const READ_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

function readString(value: unknown, key: string): string | undefined {
  if (!value || typeof value !== 'object' || !(key in value)) {
    return undefined;
  }
  const field: unknown = Reflect.get(value, key);
  return typeof field === 'string' ? field : undefined;
}

function firstError(body: unknown): unknown {
  const errors: unknown = Reflect.get(Object(body), 'errors');
  if (!Array.isArray(errors)) {
    return undefined;
  }
  const first: unknown = errors[0];
  return first;
}

/**
 * The server's message when it is short, plain text and says something
 * specific; otherwise undefined.
 */
export function getUsableServerMessage(body: unknown): string | undefined {
  const message = [
    readString(body, 'detail'),
    readString(firstError(body), 'detail'),
    readString(body, 'message')
  ].find((candidate) => candidate?.trim());
  const trimmed = message?.trim();
  if (
    !trimmed ||
    trimmed.length > MAX_SERVER_MESSAGE_LENGTH ||
    /[<\r\n]/.test(trimmed) ||
    GENERIC_SERVER_MESSAGES.has(
      withoutTrailingPunctuation(trimmed.toLowerCase())
    )
  ) {
    return undefined;
  }
  return trimmed;
}

/**
 * What people call the data behind each endpoint, so a failure can say
 * "Couldn't load filter options" instead of a line that names nothing.
 * Checked in order, so a narrower path goes before any path it is under.
 */
const ENDPOINT_NAMES: readonly { path: RegExp; what: string }[] = [
  { path: /\/si-page\/filter/, what: 'FILTER_OPTIONS' },
  { path: /\/si-page\//, what: 'SHIPMENTS' },
  { path: /\/si\/summary/, what: 'SHIPMENT_DETAILS' },
  {
    path: /\/si\/(get-plantations-by-si|link-plantations-to-si)/,
    what: 'PLOTS'
  },
  { path: /\/composition-data/, what: 'SOURCING_AREAS' },
  { path: /\/(si\/risk-layers|tile-urls|com\/tiles)/, what: 'MAP_LAYERS' },
  { path: /\/risks\//, what: 'RISK_SETTINGS' },
  { path: /\/si\/eudr-package/, what: 'EUDR_PACKAGE' },
  { path: /\/si\/ddr/, what: 'DUE_DILIGENCE_REPORTS' },
  { path: /\/trn\/legality-summary/, what: 'LEGALITY_SUMMARY' },
  { path: /\/trn\/approval/, what: 'APPROVALS' },
  { path: /\/counterparties/, what: 'COUNTERPARTIES' },
  { path: /\/contract\//, what: 'CONTRACTS' },
  { path: /\/reports\//, what: 'REPORTS' },
  { path: /\/settings/, what: 'SETTINGS' },
  { path: /\/user\/info/, what: 'ACCOUNT' },
  { path: /\/plantations\//, what: 'PLOTS' },
  { path: /\/si\//, what: 'SHIPMENTS' }
];

/** The URL up to its query string or fragment. */
function withoutQuery(url: string): string {
  const end = url.search(/[?#]/);
  return end === -1 ? url : url.slice(0, end);
}

/** "Bad request." and "Bad request!" both match "bad request". */
function withoutTrailingPunctuation(text: string): string {
  let end = text.length;
  while (end > 0 && '.!'.includes(text.charAt(end - 1))) end--;
  return text.slice(0, end);
}

// Downloads have their own wording: nothing was saved, nothing to reload.
const DOWNLOAD_ENDPOINT = /download/;

/** The `ERRORS.WHAT` key for the data a request was for, if it's one we name. */
export function getEndpointName(url: string | null): string | undefined {
  if (!url) return;
  const path = withoutQuery(url);
  const entry = ENDPOINT_NAMES.find((name) => name.path.test(path));
  return entry && `ERRORS.WHAT.${entry.what}`;
}

/** What the toast says for a failed request: what happened, then what to do. */
export function getHttpErrorToastMessage(
  error: HttpErrorResponse,
  method: string
): HttpErrorToastMessage {
  switch (error.status) {
    case 0:
      return { key: 'ERRORS.TOAST.OFFLINE' };
    case 403:
      return { key: 'ERRORS.TOAST.FORBIDDEN' };
    case 404:
      return { key: 'ERRORS.TOAST.NOT_FOUND' };
    case 400:
    case 409:
    case 422: {
      const text = getUsableServerMessage(error.error);
      return text ? { text } : { key: 'ERRORS.TOAST.INVALID' };
    }
    case 429:
      return { key: 'ERRORS.TOAST.TOO_MANY_REQUESTS' };
    default: {
      const path = withoutQuery(error.url ?? '');
      if (DOWNLOAD_ENDPOINT.test(path)) {
        return { key: 'ERRORS.TOAST.DOWNLOAD_FAILED' };
      }
      const isRead = READ_METHODS.has(method.toUpperCase());
      const what = getEndpointName(error.url);
      if (what) {
        return {
          key: isRead
            ? 'ERRORS.TOAST.LOAD_FAILED_WHAT'
            : 'ERRORS.TOAST.ACTION_FAILED_WHAT',
          what
        };
      }
      return isRead
        ? { key: 'ERRORS.TOAST.LOAD_FAILED' }
        : { key: 'ERRORS.TOAST.ACTION_FAILED' };
    }
  }
}

/**
 * What goes to Sentry for a failed request: the server's detail when there is
 * one, otherwise the error without its query string. Query parameters can
 * contain secret keys; keep them out of error reports. Anything that isn't a
 * failed request, such as a bug thrown while handling the response, goes as
 * it is, with its stack.
 */
export function toReportableError(error: unknown): unknown {
  if (!(error instanceof HttpErrorResponse)) return error;
  const body: unknown = error.error;
  const detail: unknown =
    body && typeof body === 'object' && 'detail' in body
      ? body.detail
      : undefined;
  return (
    detail ??
    new HttpErrorResponse({
      ...error,
      url: error.url?.split(/[?#]/)[0]
    })
  );
}
